import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import BrokerDashboard from './BrokerDashboard';

export default function BrokerDashboardTour() {
  const navigate = useNavigate();
  const driverRef = useRef(null);
  const [tourStarted, setTourStarted] = useState(false);

  useEffect(() => {
    // Wait for the dashboard to render
    const timer = setTimeout(() => {
      startTour();
    }, 1500);

    return () => {
      clearTimeout(timer);
      if (driverRef.current) {
        driverRef.current.destroy();
      }
    };
  }, []);

  const startTour = () => {
    // Create driver instance with custom styling
    driverRef.current = driver({
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      overlayOpacity: 0.75,
      stagePadding: 10,
      stageRadius: 5,
      allowClose: true,
      popoverClass: 'broker-tour-popover',
      steps: [
        {
          element: '[data-tour="dashboard-header"]',
          popover: {
            title: '👋 Welcome to Your Partner Dashboard',
            description: 'This is your command center for managing deals, tracking commissions, and growing your investor network. All your key performance metrics and tools are accessible from here. Let\'s walk through the main features.',
            side: 'bottom',
            align: 'start'
          }
        },
        {
          element: '[data-tour="stats-cards"]',
          popover: {
            title: '📊 Your Performance at a Glance',
            description: 'These four key metrics update in real-time:\n\n• **Capital Raised** - Total business you\'ve brought in\n• **Commission Earned** - Your total earnings\n• **Active Investors** - Investors you\'ve referred\n• **Deals Contributed** - Properties you\'ve helped fund',
            side: 'bottom',
            align: 'center'
          }
        },
        {
          element: '[data-tour="deal-selector"]',
          popover: {
            title: '🏢 Browse Available Deals',
            description: 'Here you\'ll find all live investment opportunities. Each deal card shows:\n\n• Property name and location\n• Total asset value\n• Capital target for the unit\n\nClick on any property to select it and see detailed financials in the calculator.',
            side: 'right',
            align: 'start'
          }
        },
        {
          element: '[data-tour="deal-details"]',
          popover: {
            title: '💰 Deal Financial Breakdown',
            description: 'When you select a deal, this section shows the complete financial picture:\n\n• Unit value and capital target\n• Amount already funded\n• Balance remaining for you to raise\n• Available units in this property',
            side: 'left',
            align: 'start'
          }
        },
        {
          element: '[data-tour="earnings-calculator"]',
          popover: {
            title: '🧮 Commission Calculator',
            description: 'This is your earnings estimator. Use the slider to input the capital amount you plan to raise, and instantly see:\n\n• Your commission amount in rupees\n• Applied commission rate based on your tier\n• Potential earnings at higher tiers\n\nThe more capital you raise, the higher your tier and commission rate!',
            side: 'left',
            align: 'center'
          }
        },
        {
          element: '[data-tour="tier-display"]',
          popover: {
            title: '🏆 Your Commission Tier',
            description: 'Your tier is based on total cumulative capital raised:\n\n• 🥉 **Bronze** - Starting tier (2.92%)\n• 🥈 **Silver** - ₹25L+ raised (3.50%)\n• 🥇 **Gold** - ₹75L+ raised (4.08%)\n• 💎 **Platinum** - ₹2.25Cr+ raised (4.96%)\n• 👑 **Diamond** - ₹6.75Cr+ raised (5.83%)\n\nHigher tiers mean better commission rates on every deal!',
            side: 'left',
            align: 'start'
          }
        },
        {
          element: '[data-tour="tier-progress"]',
          popover: {
            title: '📈 Track Your Progress',
            description: 'This progress bar shows how close you are to unlocking the next tier. Every deal you close brings you closer to better commission rates.\n\nTip: Focus on closing deals consistently to level up faster!',
            side: 'left',
            align: 'start'
          }
        },
        {
          element: '[data-tour="allocate-btn"]',
          popover: {
            title: '✅ Ready to Close a Deal?',
            description: 'When you have an investor ready, click this button to allocate them to the property.\n\nThe platform handles:\n• KYC verification\n• Document signing\n• Payment processing\n• Commission calculation\n\nYou focus on relationships, we handle the paperwork!',
            side: 'top',
            align: 'center'
          }
        }
      ],
      onDestroyed: () => {
        setTourStarted(false);
      },
      onDestroyStarted: () => {
        // When tour ends, redirect to regular dashboard
        setTimeout(() => {
          navigate('/broker');
        }, 500);
      }
    });

    // Start the tour
    driverRef.current.drive();
    setTourStarted(true);
  };

  const restartTour = () => {
    if (driverRef.current) {
      driverRef.current.destroy();
    }
    setTimeout(() => startTour(), 100);
  };

  return (
    <div className="relative">
      {/* Add custom CSS for driver.js - Enhanced visibility */}
      <style>{`
        .driver-popover.broker-tour-popover {
          background: #0a0a0a !important;
          border: 1px solid rgba(74, 222, 128, 0.4) !important;
          color: white !important;
          max-width: 420px !important;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.8), 0 0 30px rgba(74, 222, 128, 0.15) !important;
        }
        .driver-popover-title {
          color: #4ADE80 !important;
          font-size: 17px !important;
          font-weight: 600 !important;
          margin-bottom: 8px !important;
        }
        .driver-popover-description {
          color: #d0d0d0 !important;
          font-size: 14px !important;
          line-height: 1.7 !important;
          white-space: pre-line !important;
        }
        .driver-popover-description strong {
          color: #4ADE80 !important;
          font-weight: 600 !important;
        }
        .driver-popover-progress-text {
          color: #666 !important;
          font-size: 12px !important;
        }
        .driver-popover-navigation-btns {
          gap: 10px !important;
          margin-top: 16px !important;
        }
        .driver-popover-prev-btn,
        .driver-popover-next-btn {
          background: transparent !important;
          border: 1px solid rgba(255,255,255,0.2) !important;
          color: white !important;
          padding: 10px 20px !important;
          font-size: 12px !important;
          text-transform: uppercase !important;
          letter-spacing: 0.5px !important;
          transition: all 0.2s ease !important;
        }
        .driver-popover-prev-btn:hover {
          border-color: rgba(255,255,255,0.4) !important;
          background: rgba(255,255,255,0.05) !important;
        }
        .driver-popover-next-btn {
          background: #4ADE80 !important;
          border-color: #4ADE80 !important;
          color: black !important;
          font-weight: 600 !important;
        }
        .driver-popover-next-btn:hover {
          background: #3BD070 !important;
          border-color: #3BD070 !important;
        }
        .driver-popover-close-btn {
          color: #666 !important;
          width: 28px !important;
          height: 28px !important;
        }
        .driver-popover-close-btn:hover {
          color: white !important;
          background: rgba(255,255,255,0.1) !important;
        }
        .driver-popover-arrow-side-left {
          border-right-color: rgba(74, 222, 128, 0.4) !important;
        }
        .driver-popover-arrow-side-right {
          border-left-color: rgba(74, 222, 128, 0.4) !important;
        }
        .driver-popover-arrow-side-top {
          border-bottom-color: rgba(74, 222, 128, 0.4) !important;
        }
        .driver-popover-arrow-side-bottom {
          border-top-color: rgba(74, 222, 128, 0.4) !important;
        }
        
        /* Critical: Make highlighted element stand out clearly */
        .driver-overlay {
          background: rgba(0, 0, 0, 0.85) !important;
        }
        
        /* Highlighted element styling */
        .driver-active-element {
          background: #0a0a0a !important;
          position: relative !important;
          z-index: 10001 !important;
          box-shadow: 0 0 0 4px rgba(74, 222, 128, 0.3), 0 0 30px rgba(74, 222, 128, 0.2) !important;
          border-radius: 4px !important;
        }
        
        /* Ensure the highlighted section has solid background */
        [data-tour] {
          position: relative;
        }
        
        .driver-active-element [data-tour],
        .driver-active-element {
          background-color: #0a0a0a !important;
        }
      `}</style>
      
      <BrokerDashboard isTourMode={true} />
      
      {/* Floating restart button */}
      {!tourStarted && (
        <button
          onClick={restartTour}
          className="fixed bottom-8 right-8 bg-[#4ADE80] text-black px-6 py-3 font-semibold text-sm uppercase tracking-wider hover:bg-[#4ADE80]/90 transition-colors z-50"
        >
          Restart Tour
        </button>
      )}
    </div>
  );
}
